import { useMemo, useState, type ReactNode } from 'react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { CheckCircle2, Clock, Crown, Eye, LayoutGrid, List, Loader2, Search, Undo2, UserCheck, Users } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useLocalization } from '@/hooks/useLocalization';
import { useUserRole } from '@/hooks/useUserRole';
import { useStakeholderDirectory, type Position, type StakeholderPerson } from '@/hooks/useStakeholderDirectory';

const REVIEW_ROLES = ['pmo', 'executive', 'tenant_admin', 'admin'];

type Group = 'key-player' | 'keep-satisfied' | 'keep-informed' | 'monitor';

/** Power is the person's highest influence on any project. Only high interest counts as engaged. */
const groupOf = (p: StakeholderPerson): Group => {
  const power = p.influence === 'high';
  const interested = p.interest === 'high';
  if (power) return interested ? 'key-player' : 'keep-satisfied';
  return interested ? 'keep-informed' : 'monitor';
};

const GROUPS: Array<{ group: Group; label: string; hint: string; icon: typeof Crown; tone: string; badge: string }> = [
  { group: 'key-player', label: 'Key Players', hint: 'High power & high interest - Manage closely', icon: Crown, tone: 'bg-primary/10 text-primary border-primary/20', badge: 'bg-primary text-primary-foreground' },
  { group: 'keep-satisfied', label: 'Keep Satisfied', hint: 'High power & lower interest - Keep satisfied', icon: UserCheck, tone: 'bg-amber-500/10 text-amber-600 border-amber-500/20', badge: 'bg-amber-500 text-white' },
  { group: 'keep-informed', label: 'Keep Informed', hint: 'Lower power & high interest - Keep informed', icon: Users, tone: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20', badge: 'bg-emerald-500 text-white' },
  { group: 'monitor', label: 'Monitor', hint: 'Lower power & lower interest - Monitor', icon: Eye, tone: 'bg-muted text-muted-foreground border-border', badge: 'bg-muted text-muted-foreground' },
];

const powerWord = (p: StakeholderPerson) => (p.influence === 'high' ? 'high' : p.influence);

const mostCommon = (values: Array<string | null>): string | null => {
  const counts = new Map<string, number>();
  for (const v of values) if (v) counts.set(v, (counts.get(v) ?? 0) + 1);
  return [...counts].sort((a, b) => b[1] - a[1])[0]?.[0] ?? null;
};

const POSITION_STYLE: Record<Position, string> = {
  resistant: 'bg-destructive/15 text-destructive border-destructive/30',
  neutral: 'bg-muted text-muted-foreground border-border',
  supportive: 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30',
};

const initials = (name: string) => name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join('');

function PersonCard({ person, onOpen }: { person: StakeholderPerson; onOpen: () => void }) {
  const { t, tx } = useLocalization();
  const cfg = GROUPS.find((g) => g.group === groupOf(person)) ?? GROUPS[3];
  const names = [...new Set(person.roles.map((r) => r.projectName))];
  return (
    <button
      type="button"
      onClick={onOpen}
      className="group rounded-lg border bg-card p-4 text-start shadow-sm transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      aria-label={`${person.name}, ${t(cfg.label)}`}
    >
      <div className="flex items-start gap-3">
        <Avatar className="h-12 w-12 shrink-0">
          <AvatarFallback className="bg-primary/10 text-primary">{initials(person.name)}</AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <p className="truncate font-semibold text-foreground">{tx(person.name)}</p>
          <p className="truncate text-sm text-muted-foreground">{person.primaryRole ? tx(person.primaryRole) : t('Stakeholder')}</p>
          {person.portfolios.length > 0 && <p className="mt-0.5 truncate text-xs text-muted-foreground">{person.portfolios.length} {t('portfolios')}</p>}
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5">
        <Badge variant="outline" className={cn('whitespace-nowrap', cfg.badge)}>{t(cfg.label)}</Badge>
        <Badge variant="outline" className="whitespace-nowrap text-xs">{t('Power:')} {tx(powerWord(person))}</Badge>
        {person.interest && <Badge variant="outline" className="whitespace-nowrap text-xs">{t('Interest:')} {tx(person.interest)}</Badge>}
        {person.position && <Badge variant="outline" className={cn('whitespace-nowrap text-xs', POSITION_STYLE[person.position])}>{tx(person.position)}</Badge>}
      </div>
      <div className="mt-3">
        <p className="mb-1 text-xs text-muted-foreground">{t('Projects:')}</p>
        <div className="flex flex-wrap gap-1">
          {names.slice(0, 3).map((n) => <Badge key={n} variant="secondary" className="max-w-full truncate text-xs">{tx(n)}</Badge>)}
          {names.length > 3 && <Badge variant="outline" className="text-xs">+{names.length - 3}</Badge>}
        </div>
      </div>
    </button>
  );
}

function PersonDetail({ person }: { person: StakeholderPerson }) {
  const { t, tx } = useLocalization();
  const byPortfolio = useMemo(() => {
    const map = new Map<string, typeof person.roles>();
    for (const r of person.roles) {
      const k = r.portfolio ?? t('No portfolio');
      if (!map.has(k)) map.set(k, []);
      map.get(k)!.push(r);
    }
    return [...map];
  }, [person, t]);
  return (
    <div className="space-y-5">
      {byPortfolio.map(([portfolio, roles]) => (
        <section key={portfolio} aria-label={portfolio}>
          <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">{tx(portfolio)} · {roles.length}</h3>
          <ul className="space-y-2">
            {roles.map((r, i) => (
              <li key={`${r.projectId}-${i}`} className="rounded-md border p-3">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium leading-snug">{tx(r.projectName)}</p>
                    <p className="text-sm text-muted-foreground">{r.role ? tx(r.role) : t('Stakeholder')}</p>
                  </div>
                  {r.position && <Badge variant="outline" className={cn('shrink-0', POSITION_STYLE[r.position])}>{tx(r.position)}</Badge>}
                </div>
                <p className="mt-1.5 text-xs text-muted-foreground">
                  {[r.influence && `${t('Influence')}: ${r.influence}`, r.interest && `${t('Interest')}: ${r.interest}`, r.strategy, r.cadence].filter(Boolean).join(' · ')}
                </p>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

/**
 * The people on a tenant's projects, as cards grouped by influence. A card opens to the person's role on
 * each project and portfolio. People come straight from the project documents, so they are visible
 * at once; one button keeps them all in the register.
 */
export function StakeholderDirectory({ fallback, below }: { fallback?: ReactNode; below?: ReactNode }) {
  const { t, tx } = useLocalization();
  const { role, loading: roleLoading } = useUserRole();
  const canManage = !roleLoading && REVIEW_ROLES.includes(role ?? '');
  const { people, counts, loading, busy, error, addAll, undoAll } = useStakeholderDirectory();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [confirmUndo, setConfirmUndo] = useState(false);
  const [layout, setLayout] = useState<'cards' | 'list'>('cards');

  if (loading) return null;
  if (people.length === 0) return <>{fallback}</>;

  const q = query.trim().toLowerCase();
  const shown = people.filter((p) => !q || p.name.toLowerCase().includes(q) || (p.primaryRole ?? '').toLowerCase().includes(q)
    || p.roles.some((r) => r.projectName.toLowerCase().includes(q) || (r.portfolio ?? '').toLowerCase().includes(q) || (r.role ?? '').toLowerCase().includes(q)));
  const plans = GROUPS.map((cfg) => {
    const members = people.filter((p) => groupOf(p) === cfg.group);
    const roles = members.flatMap((p) => p.roles);
    return { cfg, members: members.length, cadence: mostCommon(roles.map((r) => r.cadence)), approach: mostCommon(roles.map((r) => r.strategy)) };
  });
  const selected = people.find((p) => p.key === open) ?? null;
  const resistantHigh = people.filter((p) => p.influence === 'high' && p.position === 'resistant').length;

  const onAddAll = async () => {
    setNotice(null);
    const res = await addAll();
    if (res) setNotice(`${res.approved} ${t('project roles added to the register')}.`);
  };
  const onUndo = async () => {
    if (!confirmUndo) { setConfirmUndo(true); return; }
    setConfirmUndo(false);
    setNotice(null);
    const res = await undoAll();
    if (res) setNotice(`${res.reverted} ${t('moved back to detected')}${res.skipped ? `; ${res.skipped} ${t('edited since were kept')}` : ''}.`);
  };

  return (
    <div className="space-y-6">
      <Card className="border-primary/30 bg-primary/5">
        <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
          <div className="min-w-0">
            <p className="text-sm font-semibold">
              {people.length} {t('people across')} {counts.projects} {t('projects')}
              {resistantHigh > 0 && <span className="ms-2 font-normal text-destructive">· {resistantHigh} {t('high-influence and resistant')}</span>}
            </p>
            <p className="text-xs text-muted-foreground">
              {counts.waiting > 0
                ? t('Read from your project stakeholder registers. Add them all to keep them in your register.')
                : t('Kept in your register from your project stakeholder registers.')}
            </p>
          </div>
          {canManage && (counts.waiting > 0 ? (
            <Button onClick={onAddAll} disabled={busy}>
              {busy ? <Loader2 className="me-1.5 h-4 w-4 animate-spin" /> : <CheckCircle2 className="me-1.5 h-4 w-4" />}
              {t('Add all to register')}
            </Button>
          ) : (
            <Button variant="ghost" size="sm" onClick={onUndo} disabled={busy} className="text-muted-foreground">
              <Undo2 className="me-1.5 h-3.5 w-3.5" />
              {confirmUndo ? t('Click again to undo') : t('Undo')}
            </Button>
          ))}
        </CardContent>
      </Card>
      {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
      {notice && <p role="status" className="text-sm text-muted-foreground">{notice}</p>}

      <div className="relative max-w-md">
        <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
        <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('Search by name, role, project or portfolio')} className="ps-10" aria-label={t('Search stakeholders')} />
      </div>

      <Tabs defaultValue="all">
        <TabsList>
          <TabsTrigger value="all">{t('All (')}{shown.length})</TabsTrigger>
          <TabsTrigger value="matrix">{t('Influence Matrix')}</TabsTrigger>
          <TabsTrigger value="communication">{t('Communication Plan')}</TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="mt-6 space-y-4">
          <div className="flex justify-end gap-1" role="group" aria-label={t('View')}>
            <Button variant={layout === 'cards' ? 'secondary' : 'ghost'} size="sm" onClick={() => setLayout('cards')} aria-pressed={layout === 'cards'}>
              <LayoutGrid className="me-1.5 h-4 w-4" />{t('Cards')}
            </Button>
            <Button variant={layout === 'list' ? 'secondary' : 'ghost'} size="sm" onClick={() => setLayout('list')} aria-pressed={layout === 'list'}>
              <List className="me-1.5 h-4 w-4" />{t('List')}
            </Button>
          </div>
          {shown.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">{t('Nobody matches that search.')}</p>
          ) : layout === 'cards' ? (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              {shown.map((p) => <PersonCard key={p.key} person={p} onOpen={() => setOpen(p.key)} />)}
            </div>
          ) : (
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('Name')}</TableHead>
                    <TableHead>{t('Role')}</TableHead>
                    <TableHead>{t('Group')}</TableHead>
                    <TableHead>{t('Power')}</TableHead>
                    <TableHead>{t('Interest')}</TableHead>
                    <TableHead>{t('Position')}</TableHead>
                    <TableHead className="text-end">{t('Projects')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {shown.map((p) => {
                    const cfg = GROUPS.find((g) => g.group === groupOf(p)) ?? GROUPS[3];
                    return (
                      <TableRow key={p.key} className="cursor-pointer" onClick={() => setOpen(p.key)}>
                        <TableCell className="font-medium">
                          <button type="button" className="text-start hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" onClick={(e) => { e.stopPropagation(); setOpen(p.key); }}>{tx(p.name)}</button>
                        </TableCell>
                        <TableCell className="max-w-[16rem] truncate text-muted-foreground">{p.primaryRole ? tx(p.primaryRole) : ''}</TableCell>
                        <TableCell><Badge variant="outline" className={cn('whitespace-nowrap', cfg.badge)}>{t(cfg.label)}</Badge></TableCell>
                        <TableCell className="capitalize">{tx(powerWord(p))}</TableCell>
                        <TableCell className="capitalize">{p.interest ? tx(p.interest) : ''}</TableCell>
                        <TableCell>{p.position && <Badge variant="outline" className={POSITION_STYLE[p.position]}>{tx(p.position)}</Badge>}</TableCell>
                        <TableCell className="text-end">{p.projectCount}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="matrix" className="mt-6">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {GROUPS.map((cfg) => {
              const members = shown.filter((p) => groupOf(p) === cfg.group);
              const Icon = cfg.icon;
              return (
                <Card key={cfg.group} className={cn('border-2', cfg.tone)}>
                  <CardContent className="space-y-3 p-5">
                    <div>
                      <h2 className="flex items-center gap-2 text-lg font-semibold">
                        <Icon className="h-5 w-5" aria-hidden="true" />
                        {t(cfg.label)}
                        <Badge variant="secondary" className="ms-auto">{members.length}</Badge>
                      </h2>
                      <p className="text-sm text-muted-foreground">{t(cfg.hint)}</p>
                    </div>
                    {members.length === 0 ? (
                      <p className="py-4 text-center text-sm text-muted-foreground">{t('No stakeholders in this category')}</p>
                    ) : (
                      <ul className="max-h-96 space-y-2 overflow-y-auto pe-1">
                        {members.map((p) => (
                          <li key={p.key}>
                            <button type="button" onClick={() => setOpen(p.key)} className="flex w-full items-center gap-3 rounded-lg bg-background/50 p-3 text-start transition-colors hover:bg-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                              <Avatar className="h-10 w-10 shrink-0"><AvatarFallback className="bg-primary/10 text-sm text-primary">{initials(p.name)}</AvatarFallback></Avatar>
                              <span className="min-w-0 flex-1">
                                <span className="block truncate text-sm font-medium">{tx(p.name)}</span>
                                <span className="block truncate text-xs text-muted-foreground">{p.primaryRole ? tx(p.primaryRole) : t('Stakeholder')} · {p.projectCount} {t('projects')}</span>
                              </span>
                              {p.position === 'resistant' && <Badge variant="outline" className={cn('shrink-0', POSITION_STYLE.resistant)}>{t('resistant')}</Badge>}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="communication" className="mt-6 space-y-6">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
            {plans.map(({ cfg, members, cadence, approach }) => {
              const Icon = cfg.icon;
              return (
                <Card key={cfg.group} className={cn('border-2', cfg.tone)}>
                  <CardContent className="space-y-4 p-5">
                    <div>
                      <h2 className="flex items-center gap-2 text-lg font-semibold"><Icon className="h-5 w-5" aria-hidden="true" />{t(cfg.label)}</h2>
                      <p className="text-sm text-muted-foreground">{members} {t('stakeholder(s)')}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-background"><Clock className="h-4 w-4 text-muted-foreground" aria-hidden="true" /></div>
                      <div>
                        <p className="text-xs text-muted-foreground">{t('Frequency')}</p>
                        <p className="font-medium">{cadence ? tx(cadence) : t('Not set in the registers')}</p>
                      </div>
                    </div>
                    <div>
                      <p className="mb-1 text-xs text-muted-foreground">{t('Engagement approach')}</p>
                      <p className="text-sm">{approach ? tx(approach) : t(cfg.hint.split(' - ')[1])}</p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('Stakeholder Group')}</TableHead>
                  <TableHead>{t('Stakeholders')}</TableHead>
                  <TableHead>{t('Frequency')}</TableHead>
                  <TableHead>{t('Engagement approach')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {plans.map(({ cfg, members, cadence, approach }) => (
                  <TableRow key={cfg.group}>
                    <TableCell><Badge className={cfg.badge}>{t(cfg.label)}</Badge></TableCell>
                    <TableCell>{members}</TableCell>
                    <TableCell>{cadence ? tx(cadence) : ''}</TableCell>
                    <TableCell>{approach ? tx(approach) : t(cfg.hint.split(' - ')[1])}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>

      <Sheet open={selected !== null} onOpenChange={(o) => { if (!o) setOpen(null); }}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-lg">
          {selected && (
            <>
              <SheetHeader className="text-start">
                <div className="flex items-center gap-3">
                  <Avatar className="h-12 w-12"><AvatarFallback className="bg-primary/10 text-primary">{initials(selected.name)}</AvatarFallback></Avatar>
                  <div className="min-w-0">
                    <SheetTitle className="truncate">{tx(selected.name)}</SheetTitle>
                    <SheetDescription>
                      {t('Influence')}: {tx(selected.influence)}{selected.interest ? ` · ${t('Interest')}: ${tx(selected.interest)}` : ''} · {selected.projectCount} {t('projects')}
                      {selected.portfolios.length > 0 ? ` · ${selected.portfolios.length} ${t('portfolios')}` : ''}
                    </SheetDescription>
                  </div>
                </div>
              </SheetHeader>
              <div className="mt-6"><PersonDetail person={selected} /></div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {below}
    </div>
  );
}

export default StakeholderDirectory;
